import * as THREE from 'three';
import { loadNetwork, loadSchedule, loadRhein } from './data/load';
import { SceneView } from './render/scene';
import { TracksLayer } from './render/tracks';
import { StructuresLayer } from './render/structures';
import { StationsLayer } from './render/stations';
import { ConnectionsLayer } from './render/connections';
import { WaterLayer } from './render/water';
import { BasemapLayer } from './render/basemap';
import { buildNodeHeights } from './geometry/paths';
import { ElevationIndex } from './geometry/elevation';
import { Fleet } from './simulation/trains';
import type { TrainInfo } from './simulation/trains';
import { SimulationClock } from './simulation/clock';
import { activeServices } from './simulation/calendar';

import { UIController, berlinNow } from './ui/ui';
import { lineColor } from './data/lineColors';
import { shouldApplyRealtime } from './realtime/realtime';
import type { RealtimeResponse, RealtimeEvent } from './realtime/realtime';
import type { ScheduleFile, Station, Connector } from './data/types';

const app = document.getElementById('app')!;
const info = document.getElementById('info')!;
const uiRoot = document.getElementById('ui')!;
const clockEl = document.getElementById('clock')!;
const speedEl = document.getElementById('speed')!;

/** Transient status line (top-centre): hidden when empty. */
function setStatus(text: string) {
  info.textContent = text;
  info.style.display = text ? 'block' : 'none';
}

/** 'YYYY-MM-DD' -> 'DD.MM.YYYY' for the clock panel. */
function formatDateGerman(dateStr: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return dateStr;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/** 'YYYYMMDD' -> 'DD.MM.YYYY'. */
function formatYmd(ymd: string): string {
  if (!/^\d{8}$/.test(ymd)) return ymd;
  return `${ymd.slice(6, 8)}.${ymd.slice(4, 6)}.${ymd.slice(0, 4)}`;
}

const INFOPANEL_POS_KEY = 'rheinbahn3d.infopanel.pos';
const INFOPANEL_SIZE_KEY = 'rheinbahn3d.infopanel.size';

/** Restore a previously saved (drag) position of the info panel. */
function restorePanelPosition(panel: HTMLElement) {
  try {
    const raw = localStorage.getItem(INFOPANEL_POS_KEY);
    if (!raw) return;
    const p = JSON.parse(raw) as { left?: unknown; top?: unknown };
    if (typeof p.left === 'number' && typeof p.top === 'number') {
      panel.style.left = `${p.left}px`;
      panel.style.top = `${p.top}px`;
    }
  } catch {
    /* ignore malformed/unavailable storage */
  }
}

/** Restore a previously saved (resized) size of the info panel. */
function restorePanelSize(panel: HTMLElement) {
  try {
    const raw = localStorage.getItem(INFOPANEL_SIZE_KEY);
    if (!raw) return;
    const s = JSON.parse(raw) as { width?: unknown; height?: unknown };
    if (typeof s.width === 'number') {
      panel.style.width = `${s.width}px`;
      panel.style.maxWidth = 'none';
    }
    if (typeof s.height === 'number') {
      panel.style.height = `${s.height}px`;
      panel.style.maxHeight = 'none';
    }
  } catch {
    /* ignore malformed/unavailable storage */
  }
}

/** Natural (content-driven) size of the panel, ignoring any explicit resize. */
function naturalPanelSize(panel: HTMLElement): { width: number; height: number } {
  const w = panel.style.width;
  const h = panel.style.height;
  const mw = panel.style.maxWidth;
  const mh = panel.style.maxHeight;
  panel.style.width = 'auto';
  panel.style.height = 'auto';
  panel.style.maxWidth = '';
  panel.style.maxHeight = '';
  const size = { width: panel.offsetWidth, height: panel.offsetHeight };
  panel.style.width = w;
  panel.style.height = h;
  panel.style.maxWidth = mw;
  panel.style.maxHeight = mh;
  return size;
}

/** Make the info panel draggable by its title bar; persist the position. */
function setupDraggablePanel(panel: HTMLElement) {
  restorePanelPosition(panel);
  let drag: { startX: number; startY: number; left: number; top: number } | null = null;

  panel.addEventListener('pointerdown', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.ip-title') || target.closest('button')) return;
    const rect = panel.getBoundingClientRect();
    drag = { startX: e.clientX, startY: e.clientY, left: rect.left, top: rect.top };
    panel.setPointerCapture(e.pointerId);
    e.preventDefault();
  });

  panel.addEventListener('pointermove', (e) => {
    if (!drag) return;
    panel.style.left = `${drag.left + (e.clientX - drag.startX)}px`;
    panel.style.top = `${drag.top + (e.clientY - drag.startY)}px`;
  });

  const finish = (e: PointerEvent) => {
    if (!drag) return;
    drag = null;
    if (panel.hasPointerCapture(e.pointerId)) panel.releasePointerCapture(e.pointerId);
    try {
      const rect = panel.getBoundingClientRect();
      localStorage.setItem(INFOPANEL_POS_KEY, JSON.stringify({ left: Math.round(rect.left), top: Math.round(rect.top) }));
    } catch {
      /* ignore */
    }
  };

  panel.addEventListener('pointerup', finish);
  panel.addEventListener('pointercancel', finish);
}

/** Make the info panel resizable via its bottom-right corner; persist the size. */
function setupResizablePanel(panel: HTMLElement): HTMLElement {
  restorePanelSize(panel);

  const handle = document.createElement('div');
  handle.className = 'ip-resize';
  handle.title = 'Größe ändern';
  panel.appendChild(handle);

  let resizing: { startX: number; startY: number; startW: number; startH: number; minW: number; minH: number } | null = null;

  handle.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const min = naturalPanelSize(panel);
    resizing = {
      startX: e.clientX,
      startY: e.clientY,
      startW: panel.offsetWidth,
      startH: panel.offsetHeight,
      minW: min.width,
      minH: min.height,
    };
    handle.setPointerCapture(e.pointerId);
  });

  handle.addEventListener('pointermove', (e) => {
    if (!resizing) return;
    const w = Math.max(resizing.minW, resizing.startW + (e.clientX - resizing.startX));
    const h = Math.max(resizing.minH, resizing.startH + (e.clientY - resizing.startY));
    panel.style.width = `${w}px`;
    panel.style.height = `${h}px`;
    panel.style.maxWidth = 'none';
    panel.style.maxHeight = 'none';
  });

  const finish = (e: PointerEvent) => {
    if (!resizing) return;
    resizing = null;
    if (handle.hasPointerCapture(e.pointerId)) handle.releasePointerCapture(e.pointerId);
    try {
      localStorage.setItem(
        INFOPANEL_SIZE_KEY,
        JSON.stringify({ width: Math.round(panel.offsetWidth), height: Math.round(panel.offsetHeight) }),
      );
    } catch {
      /* ignore */
    }
  };

  handle.addEventListener('pointerup', finish);
  handle.addEventListener('pointercancel', finish);

  return handle;
}

async function main() {
  const network = await loadNetwork();
  info.innerHTML = `Düsseldorf Stadtbahn — ${network.lines.length} Linien, ${network.stations.length} Stationen, ${network.meta.counts.edgeCount} Gleiskanten.`;

  const view = new SceneView(app);
  view.setContentBounds(network.bounds.minX, network.bounds.maxX, network.bounds.minZ, network.bounds.maxZ);

  const nodeHeights = buildNodeHeights(network);
  const elev = new ElevationIndex(network, nodeHeights);

  const basemap = new BasemapLayer(network.meta.origin.lon, network.meta.origin.lat);
  view.scene.add(basemap.group);

  const water = new WaterLayer();
  const rhein = await loadRhein();
  if (rhein) {
    water.build(rhein);
    view.root.add(water.group);
  }

  const tracks = new TracksLayer();
  tracks.build(network, nodeHeights);
  view.root.add(tracks.group);

  const structures = new StructuresLayer();
  structures.build(network, nodeHeights);
  view.root.add(structures.group);

  const stations = new StationsLayer();
  stations.build(network, elev);
  view.root.add(stations.group);

  const connections = new ConnectionsLayer();
  connections.build(network);
  view.root.add(connections.group);

  const fleet = new Fleet();
  view.root.add(fleet.group);

  // highlight tube for the route ahead of the currently selected train
  const routeHighlight = new THREE.Mesh(
    new THREE.BufferGeometry(),
    new THREE.MeshBasicMaterial({ color: 0xffe000, transparent: true, opacity: 0.85 }),
  );
  routeHighlight.visible = false;
  routeHighlight.renderOrder = 5;
  view.root.add(routeHighlight);

  // debug/testing handle (used by scripts/smoke.mjs to assert the scene graph)
  (window as unknown as { __app?: Record<string, unknown> }).__app = {
    network, view, tracks, structures, stations, connections, water, basemap, fleet,
  };

  const clock = new SimulationClock();
  const selectedLines = new Set<string>();
  let selectedStationId: string | null = null;
  let scheduleCache = new Map<string, Awaited<ReturnType<typeof loadSchedule>>>();
  let appliedDateStr = '';
  let appliedDate = new Date();
  let realtimeResponse: RealtimeResponse | null = null;
  let realtimeFetchedDate = '';
  let realtimeApplied = false;
  let realtimeByTrip = new Map<string, RealtimeEvent>();

  // initial camera: Düsseldorf, north-up
  view.flyTo(network.center.x, network.center.z, 5000, 0.9, 0, 0);

  // modal for the "fetch current schedule data" instructions
  const dataModal = document.createElement('div');
  dataModal.id = 'data-modal';
  dataModal.className = 'modal hidden';
  dataModal.innerHTML =
    `<div class="modal-backdrop"></div>` +
    `<div class="modal-dialog">` +
    `<div class="modal-header"><span class="modal-title">Aktuelle Fahrplandaten holen</span><button class="modal-close" id="data-modal-close" aria-label="Schließen">×</button></div>` +
    `<div class="modal-body" id="data-modal-body"></div>` +
    `</div>`;
  app.appendChild(dataModal);

  const closeDataModal = () => dataModal.classList.add('hidden');
  document.getElementById('data-modal-close')!.addEventListener('click', closeDataModal);
  (dataModal.querySelector('.modal-backdrop') as HTMLElement).addEventListener('click', closeDataModal);
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !dataModal.classList.contains('hidden')) closeDataModal();
  });

  function openDataModal(maxDisplay: string) {
    const body = document.getElementById('data-modal-body') as HTMLElement;
    body.innerHTML =
      `<p>Die Fahrplandaten liegen nur bis <b>${maxDisplay}</b> vor. So aktualisierst du sie:</p>` +
      `<ol>` +
      `<li><b>Alte Daten sichern:</b> die Ordner <code>data/</code> und <code>public/data/schedule/</code> als Backup kopieren.</li>` +
      `<li><b>Aktuelles GTFS herunterladen:</b> das VRR-Open-Data-Zip (z. B. von opendata-oepnv.de).</li>` +
      `<li><b>Extrahieren:</b> daraus <code>data/schedule.gtfs.json</code> und <code>data/stops.gtfs.json</code> erzeugen (siehe <code>scripts/extract_gtfs.py</code>).</li>` +
      `<li><b>Fahrpläne neu bauen:</b> <code>npm run data:schedule</code></li>` +
      `<li><b>App bauen:</b> <code>npm run build</code></li>` +
      `<li><b>Hochladen:</b> den Inhalt von <code>dist/</code> erneut auf den Webserver laden.</li>` +
      `</ol>`;
    dataModal.classList.remove('hidden');
  }

  function showScheduleOutOfRange(maxEnd: string) {
    const maxDisplay = formatYmd(maxEnd);
    info.innerHTML =
      `<b>Keine aktuellen Fahrplandaten.</b> Fahrpläne nur bis ${maxDisplay}. ` +
      `<button id="fetch-schedule-btn">Aktuelle Fahrplandaten holen</button>`;
    info.style.display = 'block';
    document.getElementById('fetch-schedule-btn')!.addEventListener('click', () => openDataModal(maxDisplay));
  }

  async function applyScheduled(dateStr: string, timeStr: string) {
    appliedDateStr = dateStr;
    const refs = selectedLines.size ? [...selectedLines] : network.lines.map((l) => l.ref);
    const [h, m] = timeStr.split(':').map(Number);
    const date = new Date(`${dateStr}T${timeStr}:00`);
    appliedDate = date;
    clock.time = h * 3600 + m * 60;
    setStatus(`Fahrpläne werden geladen… (${refs.length} Linien)`);
    const schedules: ScheduleFile[] = [];
    for (const ref of refs) {
      let sched = scheduleCache.get(ref);
      if (!sched) {
        try { sched = await loadSchedule(ref); scheduleCache.set(ref, sched); } catch { continue; }
      }
      schedules.push(sched);
    }
    if (schedules.length === 0) {
      setStatus(`Fehler: Fahrpläne konnten nicht geladen werden (data/schedule/*.json fehlt oder nicht erreichbar).`);
      return;
    }
    // latest calendar end date across the loaded schedules
    let maxEnd = '';
    for (const s of schedules) {
      for (const c of s.calendar) {
        if (c.end_date > maxEnd) maxEnd = c.end_date;
      }
    }
    if (maxEnd && dateStr.replace(/-/g, '') > maxEnd) {
      fleet.clear();
      showScheduleOutOfRange(maxEnd);
      return;
    }
    fleet.setScheduledMulti(network, elev, schedules, date, selectedLines.size ? selectedLines : null);
    const activeNow = fleet.activeCount(clock.time);
    if (activeNow === 0) {
      setStatus(`Keine Fahrt in diesem Dienstzeitfenster (${dateStr} ${timeStr}). Wähle eine andere Uhrzeit.`);
    } else {
      setStatus('');
    }
  }

  const ui = new UIController(uiRoot, network, {
    onToggleLine: (ref, on) => {
      if (on) selectedLines.delete(ref); else selectedLines.add(ref);
      refreshSelection();
    },
    onAllLines: (on) => {
      if (on) selectedLines.clear();
      else network.lines.forEach((l) => selectedLines.add(l.ref));
      ui.setLinesSelected(on ? network.lines.map((l) => l.ref) : []);
      refreshSelection();
    },
    onFocusLine: (ref) => {
      selectedLines.clear();
      selectedLines.add(ref);
      ui.setLinesSelected([ref]);
      refreshSelection();
      const path = network.paths.filter((p) => p.line === ref).sort((a, b) => b.length - a.length)[0];
      if (path) {
        const mid = path.coords[Math.floor(path.coords.length / 2)];
        view.flyTo(mid[0], mid[1], 6000, 0.8, 200);
      }
    },
    onSelectStation: (id) => focusStation(id),
    onPreset: (name) => applyPreset(name),
    onOverheight: (v) => view.setVerticalScale(v),
    onGroundOpacity: (v) => view.setGroundOpacity(v),
    onViewingDistance: (v) => fleet.setViewingDistance(viewingDistanceForLevel(v)),
    onToggleRhein: (on) => water.setVisible(on && water.available),
    onToggleBasemap: (on) => { basemap.setVisible(on); fleet.setBasemapOn(on); view.setGroundVisible(!on); tracks.setUndergroundOverlay(on); },
    onToggleLevels: (on) => { stations.setLevelsVisible(on); connections.setVisible(on); },
    onToggleExplode: (on) => { stations.setExplode(on); connections.setExplode(on); },
    onTogglePause: () => { clock.paused = !clock.paused; },
    onSpeed: (v) => { clock.speed = v; },
    onScheduledApply: (d, t) => applyScheduled(d, t),
  });

  // levels/connectors are opt-in (hidden until toggled on)
  stations.setLevelsVisible(false);
  connections.setVisible(false);

  // schedule is the only mode: load the timetable for "now" right away
  const now = berlinNow();
  ui.setScheduledDate(now.date, now.time);
  applyScheduled(now.date, now.time);

  function refreshSelection() {
    const sel = selectedLines;
    tracks.setLineSelection(sel);
    fleet.setLineSelection(sel);
  }

  function focusStation(id: string) {
    const st = network.stations.find((s) => s.id === id);
    if (!st) return;
    selectedStationId = id;
    view.flyTo(st.x, st.z, 170, 0.6, 230, 320);
    stations.select(id);
  }

  function applyPreset(name: string) {
    if (name === 'Gesamtnetz') {
      const cx = (network.bounds.minX + network.bounds.maxX) / 2;
      const cz = (network.bounds.minZ + network.bounds.maxZ) / 2;
      const size = Math.max(network.bounds.maxX - network.bounds.minX, network.bounds.maxZ - network.bounds.minZ);
      view.flyToPreservingOrientation(cx, cz, size * 0.9, 200);
    } else if (name === 'Düsseldorf') {
      view.flyToPreservingOrientation(network.center.x, network.center.z, 5000, 200);
    } else if (name === 'Innenstadt') {
      view.flyToPreservingOrientation(network.center.x, network.center.z, 1500, 240);
    }
  }

  // click picking
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const toPointer = (clientX: number, clientY: number) => {
    const rect = view.renderer.domElement.getBoundingClientRect();
    pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  };

  // info panel (replaces hover + train modals)
  const infopanel = document.createElement('div');
  infopanel.id = 'infopanel';
  app.appendChild(infopanel);
  setupDraggablePanel(infopanel);
  const resizeHandle = setupResizablePanel(infopanel);

  let trackedRun: number | null = null;
  let selectedTrain: number | null = null;

  function reappendResizeHandle() {
    if (!infopanel.contains(resizeHandle)) infopanel.appendChild(resizeHandle);
  }

  function hidePanel() {
    infopanel.style.display = 'none';
    selectedTrain = null;
  }

  function showStationPanel(st: Station) {
    selectedTrain = null;
    infopanel.innerHTML = `<div class="ip-title">Bahnhof</div><div class="ip-body">${ui.stationHtml(st)}</div>`;
    const body = infopanel.querySelector('.ip-body') as HTMLElement;
    const departuresDiv = document.createElement('div');
    departuresDiv.className = 'ip-departures';
    departuresDiv.innerHTML = '<b>Abfahrten</b><div class="ip-departures-list"><span class="dim">Lade Abfahrten…</span></div>';
    body.appendChild(departuresDiv);
    reappendResizeHandle();
    infopanel.style.display = 'flex';
    const stationId = st.id;
    departuresForStation(st).then(
      (deps) => {
        if (selectedStationId !== stationId) return;
        renderDepartures(departuresDiv, deps);
      },
      () => {
        if (selectedStationId !== stationId) return;
        renderDepartures(departuresDiv, []);
      },
    );
  }

  interface StationDeparture {
    line: string;
    timeSec: number;
    direction: string;
  }

  async function departuresForStation(st: Station): Promise<StationDeparture[]> {
    const out: StationDeparture[] = [];
    const lines = st.lines.length ? st.lines : network.lines.map((l) => l.ref);
    const patternById = new Map(network.patterns.map((p) => [p.id, p]));
    for (const ref of lines) {
      let sched = scheduleCache.get(ref);
      if (!sched) {
        try { sched = await loadSchedule(ref); scheduleCache.set(ref, sched); } catch { continue; }
      }
      const active = activeServices(sched, appliedDate);
      for (const trip of sched.trips) {
        if (!active.has(trip.serviceId)) continue;
        const pattern = patternById.get(trip.patternId);
        if (!pattern) continue;
        let idx = -1;
        for (let i = 0; i < pattern.stops.length; i++) {
          const stop = pattern.stops[i];
          if (stop.stationId === st.id || st.platforms.some((p) => p.stopId === stop.stopId)) {
            idx = i;
            break;
          }
        }
        if (idx < 0 || idx >= trip.times.length) continue;
        const dep = trip.times[idx][1];
        const destination = pattern.stops[pattern.stops.length - 1]?.name ?? '';
        out.push({ line: ref, timeSec: dep, direction: destination });
      }
    }
    return out
      .filter((d) => d.timeSec >= clock.time)
      .sort((a, b) => a.timeSec - b.timeSec)
      .slice(0, 12);
  }

  function renderDepartures(container: HTMLElement, deps: StationDeparture[]) {
    const list = container.querySelector('.ip-departures-list');
    if (!list) return;
    list.innerHTML = '';
    if (!deps.length) {
      const empty = document.createElement('span');
      empty.className = 'dim';
      empty.textContent = 'Keine Abfahrten im Dienstzeitfenster.';
      list.appendChild(empty);
      return;
    }
    for (const dep of deps) {
      const row = document.createElement('div');
      row.className = 'ip-dep';
      const badge = document.createElement('span');
      badge.className = 'line-badge';
      badge.style.background = lineColor(dep.line);
      badge.textContent = dep.line;
      const time = document.createElement('span');
      time.className = 'ip-dep-time';
      time.textContent = formatSecOfDayShort(dep.timeSec);
      const dir = document.createElement('span');
      dir.className = 'ip-dep-dir';
      dir.textContent = dep.direction;
      row.append(badge, time, dir);
      list.appendChild(row);
    }
  }

  function showConnectorPanel(c: Connector) {
    selectedTrain = null;
    infopanel.innerHTML = `<div class="ip-title">Verbindungsgang</div><div class="ip-body">${ui.connectorHtml(c)}</div>`;
    reappendResizeHandle();
    infopanel.style.display = 'flex';
  }

  function showTrainPanel(runIndex: number) {
    const infoData = fleet.infoFor(runIndex, clock.time);
    if (!infoData) return;
    selectedTrain = runIndex;
    infopanel.innerHTML = '';
    const head = document.createElement('div');
    head.className = 'ip-title';
    const badge = document.createElement('span');
    badge.className = 'line-badge';
    badge.style.background = lineColor(infoData.line);
    badge.textContent = infoData.line;
    head.appendChild(badge);
    head.append(' Zug');
    const btn = document.createElement('button');
    btn.textContent = 'Zug tracken';
    btn.className = 'track-btn';
    btn.onclick = (e) => {
      e.stopPropagation();
      startTracking(runIndex);
    };
    head.appendChild(btn);
    infopanel.appendChild(head);
    const body = document.createElement('div');
    body.className = 'ip-body';
    body.innerHTML =
      `Ziel: <b>${infoData.destination}</b><br>` +
      `Letzter Halt: <span class="ip-cur">${infoData.currentStop ?? '—'}</span><br>` +
      `Nächster Halt: <span class="ip-next">${infoData.nextStop ?? '—'}</span><br>` +
      `Geschwindigkeit: <span class="ip-speed">${infoData.speedKmh.toFixed(0)}</span> km/h`;
    const rt = matchedRealtimeFor(infoData.tripId);
    if (rt) {
      const rtDiv = document.createElement('div');
      rtDiv.className = 'ip-realtime';
      rtDiv.innerHTML =
        `<b>Echtzeit-Prognose${rt.stationName ? ` · ${rt.stationName}` : ''}</b><br>` +
        `Sollabfahrt: ${formatBerlinTime(rt.plannedDeparture)}<br>` +
        `progn. Abfahrt: ${formatBerlinTime(rt.estimatedDeparture)}<br>` +
        `Verspätung: ${rt.delaySec != null ? formatDelaySec(rt.delaySec) : '—'}<br>` +
        `letzter Abruf: ${formatBerlinTime(rt.fetchedAt)}<br>` +
        `<span class="dim">Position weiterhin aus Fahrplan berechnet</span>`;
      body.appendChild(rtDiv);
    }
    const stops = fleet.runStops(runIndex);
    if (stops && stops.length) {
      const routeDiv = document.createElement('div');
      routeDiv.className = 'ip-route';
      const routeTitle = document.createElement('b');
      routeTitle.textContent = 'Streckenverlauf';
      routeDiv.appendChild(routeTitle);
      const list = document.createElement('div');
      list.className = 'ip-stops';
      for (const s of stops) {
        const row = document.createElement('div');
        row.className = 'ip-stop';
        const dot = document.createElement('span');
        dot.className = 's-dot';
        dot.textContent = '·';
        const name = document.createElement('span');
        name.className = 's-name';
        name.textContent = s.name;
        const time = document.createElement('span');
        time.className = 's-time';
        time.textContent = s.arrivalSec != null ? formatSecOfDayShort(s.arrivalSec) : '—';
        const eta = document.createElement('span');
        eta.className = 's-eta';
        eta.textContent = '';
        row.append(dot, name, time, eta);
        list.appendChild(row);
      }
      routeDiv.appendChild(list);
      body.appendChild(routeDiv);
    }
    infopanel.appendChild(body);
    reappendResizeHandle();
    infopanel.style.display = 'flex';
    updateRouteList(infoData);
  }

  function updateRouteList(infoData: TrainInfo) {
    const list = infopanel.querySelector('.ip-stops');
    if (!list || selectedTrain == null) return;
    const stops = fleet.runStops(selectedTrain);
    if (!stops) return;
    const items = list.querySelectorAll('.ip-stop');
    const d = infoData.distM;
    let currentIdx = -1;
    for (let i = 0; i < stops.length; i++) if (stops[i].distM <= d + 1) currentIdx = i;
    for (let i = 0; i < items.length && i < stops.length; i++) {
      const item = items[i] as HTMLElement;
      item.classList.toggle('passed', i < currentIdx);
      item.classList.toggle('current', i === currentIdx);
      const dot = item.querySelector('.s-dot');
      if (dot) dot.textContent = i === currentIdx ? '●' : '·';
      const eta = item.querySelector('.s-eta');
      if (eta) {
        if (i <= currentIdx || stops[i].arrivalSec == null) eta.textContent = '';
        else {
          const sec = stops[i].arrivalSec! - clock.time;
          eta.textContent = sec > 0 ? `+${formatDur(sec)}` : '';
        }
      }
    }
  }

  function updateTrainPanel() {
    if (selectedTrain == null) return;
    const infoData = fleet.infoFor(selectedTrain, clock.time);
    if (!infoData) {
      hidePanel();
      return;
    }
    const cur = infopanel.querySelector('.ip-cur');
    const next = infopanel.querySelector('.ip-next');
    const speed = infopanel.querySelector('.ip-speed');
    if (cur) cur.textContent = infoData.currentStop ?? '—';
    if (next) next.textContent = infoData.nextStop ?? '—';
    if (speed) speed.textContent = infoData.speedKmh.toFixed(0);
    updateRouteList(infoData);
  }

  function updateRouteHighlight() {
    if (selectedTrain == null) {
      routeHighlight.visible = false;
      return;
    }
    const infoData = fleet.infoFor(selectedTrain, clock.time);
    const pts = infoData ? fleet.aheadRoutePoints(selectedTrain, infoData.distM) : null;
    if (!pts || pts.length < 2) {
      routeHighlight.visible = false;
      return;
    }
    const color = new THREE.Color(0xffe000);
    (routeHighlight.material as THREE.MeshBasicMaterial).color.copy(color);
    const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.5);
    const radius = 1.6;
    const segments = Math.max(16, Math.min(400, pts.length * 2));
    const newGeo = new THREE.TubeGeometry(curve, segments, radius, 6, false);
    routeHighlight.geometry.dispose();
    routeHighlight.geometry = newGeo;
    routeHighlight.visible = true;
  }

  function matchedRealtimeFor(tripId: string | null | undefined): RealtimeEvent | null {
    return tripId ? realtimeByTrip.get(tripId) ?? null : null;
  }

  function applyRealtimeToFleet() {
    const map = new Map<string, number>();
    for (const [tripId, e] of realtimeByTrip) {
      if (e.delaySec != null) map.set(tripId, e.delaySec);
    }
    fleet.setRealtimeDelays(map);
  }

  function syncRealtime() {
    if (!realtimeResponse) return;
    const applicable = shouldApplyRealtime(appliedDateStr, realtimeFetchedDate, clock.speed);
    if (applicable && !realtimeApplied) {
      applyRealtimeToFleet();
      realtimeApplied = true;
    } else if (!applicable && realtimeApplied) {
      fleet.clearRealtime();
      realtimeApplied = false;
    }
  }

  function startTracking(runIndex: number) {
    trackedRun = runIndex;
    const infoData = fleet.infoFor(runIndex, clock.time);
    setStatus(`Tracking ${infoData?.line ?? ''} ${infoData ? `→ ${infoData.destination}` : ''} — ESC oder Klick beendet.`);
  }

  function stopTracking() {
    trackedRun = null;
    view.stopFollow();
    setStatus('');
  }

  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (trackedRun != null) stopTracking();
    else hidePanel();
  });

  // hover: just a pointer cursor (no modal anymore)
  let hoverRaf = 0;
  let hoverX = 0;
  let hoverY = 0;
  function updateHover() {
    hoverRaf = 0;
    toPointer(hoverX, hoverY);
    raycaster.setFromCamera(pointer, view.camera);
    const hit =
      raycaster.intersectObjects(stations.pickTargets, false).length > 0 ||
      raycaster.intersectObjects(connections.pickTargets, false).length > 0 ||
      raycaster.intersectObjects(fleet.pickTargets, false).length > 0;
    view.renderer.domElement.style.cursor = hit ? 'pointer' : 'default';
  }
  view.renderer.domElement.addEventListener('pointermove', (ev) => {
    hoverX = ev.clientX;
    hoverY = ev.clientY;
    if (!hoverRaf) hoverRaf = requestAnimationFrame(updateHover);
  });
  view.renderer.domElement.addEventListener('pointerleave', () => {
    if (hoverRaf) { cancelAnimationFrame(hoverRaf); hoverRaf = 0; }
    view.renderer.domElement.style.cursor = 'default';
  });

  view.renderer.domElement.addEventListener('click', (ev) => {
    toPointer(ev.clientX, ev.clientY);
    raycaster.setFromCamera(pointer, view.camera);
    const trainHits = raycaster.intersectObjects(fleet.pickTargets, false);
    if (trainHits.length) {
      const idx = fleet.runIndexFor(trainHits[0].object);
      if (idx != null) {
        showTrainPanel(idx);
        return;
      }
    }
    // click elsewhere ends tracking
    if (trackedRun != null) stopTracking();
    const hits = raycaster.intersectObjects(stations.pickTargets, false);
    if (hits.length) {
      const st = stations.stationFor(hits[0].object);
      if (st) {
        focusStation(st.id);
        showStationPanel(st);
        return;
      }
    }
    const connHits = raycaster.intersectObjects(connections.pickTargets, false);
    if (connHits.length) {
      const c = connections.connectorFor(connHits[0].object);
      if (c) {
        showConnectorPanel(c);
        return;
      }
    }
    hidePanel();
  });

  // overlay: north arrow + scale bar
  const north = document.createElement('div');
  north.id = 'north';
  north.innerHTML = '<div class="arrow"><span class="n">N</span></div>';
  app.appendChild(north);
  const scale = document.createElement('div');
  scale.id = 'scalebar';
  app.appendChild(scale);
  const attrib = document.createElement('div');
  attrib.id = 'attrib';
  attrib.innerHTML = '© OpenStreetMap contributors (ODbL) · VRR GTFS (CC-BY)';
  app.appendChild(attrib);

  // legal line: copyright + impressum link
  const legal = document.createElement('div');
  legal.id = 'legal';
  legal.innerHTML = `© ${new Date().getFullYear()} Christian Hänsel · <a href="#" id="impressum-link">Impressum</a>`;
  app.appendChild(legal);

  // impressum modal
  const modal = document.createElement('div');
  modal.id = 'modal';
  modal.className = 'modal hidden';
  modal.innerHTML =
    `<div class="modal-backdrop"></div>` +
    `<div class="modal-dialog">` +
    `<div class="modal-header"><span class="modal-title">Impressum</span><button class="modal-close" id="modal-close" aria-label="Schließen">×</button></div>` +
    `<div class="modal-body" id="modal-body">Lade…</div>` +
    `</div>`;
  app.appendChild(modal);

  const closeImpressum = () => modal.classList.add('hidden');
  const openImpressum = async () => {
    const body = document.getElementById('modal-body') as HTMLElement;
    try {
      const res = await fetch('./impressum.md');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      body.innerHTML = renderMarkdown(await res.text());
    } catch {
      body.innerHTML = '<div>Impressum konnte nicht geladen werden.</div>';
    }
    modal.classList.remove('hidden');
  };
  document.getElementById('impressum-link')!.addEventListener('click', (e) => {
    e.preventDefault();
    void openImpressum();
  });
  document.getElementById('modal-close')!.addEventListener('click', closeImpressum);
  (modal.querySelector('.modal-backdrop') as HTMLElement).addEventListener('click', closeImpressum);
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.classList.contains('hidden')) closeImpressum();
  });

  // resizable sidebar: drag the left edge to change the width (persisted)
  const UI_MIN_WIDTH = 200;
  const UI_MAX_WIDTH = 600;
  const UI_WIDTH_KEY = 'rheinbahn3d.sidebar.width';

  const uiResize = document.createElement('div');
  uiResize.id = 'ui-resize';
  app.appendChild(uiResize);

  let sidebarWidth = 216;
  try {
    const raw = localStorage.getItem(UI_WIDTH_KEY);
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n)) sidebarWidth = Math.min(UI_MAX_WIDTH, Math.max(UI_MIN_WIDTH, n));
    }
  } catch {
    /* ignore */
  }

  const applySidebarWidth = (w: number) => {
    sidebarWidth = w;
    uiRoot.style.width = `${w}px`;
    uiResize.style.right = `${w}px`;
    attrib.style.right = `${w + 8}px`;
  };
  applySidebarWidth(sidebarWidth);

  let uiResizing: { startX: number; startW: number } | null = null;
  uiResize.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    uiResizing = { startX: e.clientX, startW: sidebarWidth };
    uiResize.setPointerCapture(e.pointerId);
  });
  uiResize.addEventListener('pointermove', (e) => {
    if (!uiResizing) return;
    const w = Math.min(UI_MAX_WIDTH, Math.max(UI_MIN_WIDTH, uiResizing.startW - (e.clientX - uiResizing.startX)));
    applySidebarWidth(w);
  });
  const finishUiResize = (e: PointerEvent) => {
    if (!uiResizing) return;
    uiResizing = null;
    if (uiResize.hasPointerCapture(e.pointerId)) uiResize.releasePointerCapture(e.pointerId);
    try {
      localStorage.setItem(UI_WIDTH_KEY, String(Math.round(sidebarWidth)));
    } catch {
      /* ignore */
    }
  };
  uiResize.addEventListener('pointerup', finishUiResize);
  uiResize.addEventListener('pointercancel', finishUiResize);

  // animation loop
  let last = performance.now();
  function frame(now: number) {
    const dt = now - last;
    last = now;
    clock.advance(dt);
    fleet.update(clock.time, view.camera);
    if (trackedRun != null) {
      const p = fleet.positionOf(trackedRun);
      if (p) view.follow(p);
      else stopTracking();
    }
    stations.updateLabels(view.camera.position, selectedStationId, selectedLines.size > 0);
    basemap.update(view.controls.target.x, view.controls.target.z, view.camera as THREE.PerspectiveCamera);
    updateTrainPanel();
    updateRouteHighlight();
    syncRealtime();
    clockEl.textContent = `${formatDateGerman(appliedDateStr)} · ${clock.formatted}`;
    if (Math.abs(clock.speed - 1) < 1e-9) {
      speedEl.classList.add('hidden');
      speedEl.textContent = '';
    } else {
      speedEl.textContent = `${clock.speed}×`;
      speedEl.classList.remove('hidden');
    }
    ui.setTime(clock.formatted + (view.getVerticalScale() !== 1 ? `  (Überhöhung ${view.getVerticalScale()}×)` : ''));
    // north arrow: project the world-north vector (-z) into the view plane
    const fwd = new THREE.Vector3();
    view.camera.getWorldDirection(fwd);
    const theta = Math.atan2(-fwd.x, fwd.y * fwd.z);
    (north.querySelector('.arrow') as HTMLElement).style.transform = `rotate(${theta}rad)`;
    // scale bar: meters per pixel at target distance
    const dist = view.camera.position.distanceTo(view.controls.target);
    const metersPerPixel = (2 * Math.tan((view.camera.fov * Math.PI) / 360) * dist) / view.renderer.domElement.clientHeight;
    const barMeters = niceMeters(metersPerPixel * 120);
    scale.textContent = `${barMeters} m  (Überhöhung ${view.getVerticalScale()}×)`;
    scale.style.width = `${(barMeters / metersPerPixel).toFixed(0)}px`;
    view.render();
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

function niceMeters(v: number): number {
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10;
  return step * pow;
}

function formatBerlinTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('hour')}:${get('minute')}:${get('second')}`;
}

function formatDelaySec(sec: number): string {
  const sign = sec >= 0 ? '+' : '';
  if (Math.abs(sec) < 60) return `${sign}${sec}s`;
  return `${sign}${Math.round(sec / 60)} min`;
}

/** Seconds-of-day -> 'HH:MM' (rolls over 24:00 like the clock). */
function formatSecOfDayShort(sec: number): string {
  const day = Math.floor(sec / 86400);
  const rest = sec - day * 86400;
  const h = Math.floor(rest / 3600);
  const m = Math.floor((rest % 3600) / 60);
  return `${String(h + day * 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Seconds -> compact 'Xm Ys' (or 'Ys'), non-negative. */
function formatDur(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m === 0 ? `${r}s` : `${m}m${r ? ` ${r}s` : ''}`;
}

/** Map the 1–10 "Zug-Sichtweite" slider to a viewing distance in metres. */
function viewingDistanceForLevel(level: number): number {
  const MIN = 500;
  const MAX = 20000;
  const t = Math.min(1, Math.max(0, (level - 1) / 9));
  return Math.round(MIN * Math.pow(MAX / MIN, t));
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Minimal, safe markdown renderer for the impressum (headings + bold + links). */
function renderMarkdown(md: string): string {
  const inline = (s: string) => escapeHtml(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const out: string[] = [];
  for (const raw of md.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      out.push(`<h${level}>${inline(h[2])}</h${level}>`);
    } else {
      out.push(`<div>${inline(line)}</div>`);
    }
  }
  return out.join('');
}

void main();
